import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { LocaleService } from './locale.service';

/**
 * Ajoute `Accept-Language` à **toutes** les requêtes sortantes, d'après la
 * langue active du switch FR/EN (`LocaleService.lang`).
 *
 * Objectif : que les messages produits par le back (erreurs de validation,
 * messages métier) arrivent déjà dans la langue de l'utilisateur. Le front ne
 * peut pas les traduire — ce ne sont pas des clés mais du texte final (cf.
 * `toErrorMessage`, qui renvoie `err.error.message` tel quel).
 *
 * Notes :
 * - S'applique **aussi** aux endpoints d'auth : un « identifiants invalides »
 *   renvoyé par le login doit suivre la langue comme le reste.
 * - N'écrase jamais un `Accept-Language` déjà posé explicitement par un appelant.
 * - La valeur suit la RFC 9110 : langue préférée en tête, l'autre en repli
 *   pondéré — si le back ne connaît pas la première, il peut retomber.
 * - ⚠️ Côté back, cela suppose un `LocaleResolver` basé sur l'en-tête + des
 *   bundles de messages `en`. Sans ça, l'en-tête est simplement ignoré (aucune
 *   régression : les réponses restent en français).
 * - ⚠️ Les notifications ne sont **pas** concernées : elles portent `messageFR`
 *   **et** `messageEN` en base, et le choix se fait côté front
 *   (`notificationText` / `currentLang()`), pas via cet en-tête.
 */
export const localeInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.headers.has('Accept-Language')) {
    return next(req);
  }

  const lang = inject(LocaleService).lang();
  const value = lang === 'en' ? 'en,fr;q=0.8' : 'fr,en;q=0.8';

  return next(req.clone({ setHeaders: { 'Accept-Language': value } }));
};
